import { useState, useEffect } from "react";
import { useSignIn } from "@clerk/clerk-react";
import { useNavigate, useSearchParams, Link } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { getValidRedirectUrl } from "@/lib/redirect-origins";

/* ─── Social Icons ─── */
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 1 12c0 1.74.36 3.38 1 4.89l3.84-2.8z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path
      fill="#0A66C2"
      d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
    />
  </svg>
);

export function LoginForm() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = getValidRedirectUrl(searchParams.get("redirect_url"));
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [step, setStep] = useState<"email" | "otp">("email");

  /* ─── Show error toast if redirected from failed OAuth (no account) ─── */
  useEffect(() => {
    if (searchParams.get("error") === "no-account") {
      toast.error("Account doesn't exist. Please sign up first.", {
        action: {
          label: "Get started",
          onClick: () => navigate("/get-started"),
        },
      });
      // Clean up the URL
      searchParams.delete("error");
      const newUrl = searchParams.toString()
        ? `${window.location.pathname}?${searchParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  /* ─── Social Login ─── */
  const handleSocialLogin = async (strategy: "oauth_google" | "oauth_linkedin_oidc") => {
    if (!isLoaded) return;
    setSocialLoading(strategy);
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: "/sso-callback",
        redirectUrlComplete: redirectUrl ?? "/dashboard",
      });
    } catch (err: any) {
      console.error("Social login error:", err);
      const errorCode = err.errors?.[0]?.code;
      if (errorCode === "form_identifier_not_found") {
        toast.error("Account doesn't exist. Please sign up first.", {
          action: {
            label: "Get started",
            onClick: () => navigate("/get-started"),
          },
        });
      } else {
        toast.error(err.errors?.[0]?.longMessage || "Social login failed. Please try again.");
      }
      setSocialLoading(null);
    }
  };

  /* ─── Email Step ─── */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !email) return;
    setIsLoading(true);

    try {
      const { supportedFirstFactors } = await signIn.create({
        identifier: email,
      });

      const emailCodeFactor = supportedFirstFactors?.find(
        (factor) => factor.strategy === "email_code"
      );

      if (emailCodeFactor) {
        const { emailAddressId } = emailCodeFactor as any;
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId,
        });
        setStep("otp");
        toast.success(`Code sent to ${email}`);
      } else {
        toast.error("Email code login is not available for this account.");
      }
    } catch (err: any) {
      console.error("Email submit error:", err);
      const errorCode = err.errors?.[0]?.code;
      if (errorCode === "form_identifier_not_found") {
        toast.error("Account doesn't exist. Please sign up first.", {
          action: {
            label: "Get started",
            onClick: () => navigate("/get-started"),
          },
        });
      } else {
        toast.error(err.errors?.[0]?.longMessage || "Could not continue. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  /* ─── OTP Step ─── */
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !otp) return;
    setIsLoading(true);

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code: otp,
      });

      if (result.status === "complete") {
        await setActive({ session: result.createdSessionId });
        toast.success("Welcome back!");
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          navigate("/dashboard");
        }
      } else {
        toast.error("Verification incomplete. Try again.");
      }
    } catch (err: any) {
      console.error("OTP error:", err);
      toast.error(err.errors?.[0]?.longMessage || "Invalid code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  /* ─── Email + Social Step ─── */
  if (step === "email") {
    return (
      <div className="w-full space-y-6">
        <div className="space-y-1.5">
          <h1 className="text-[1.75rem] font-bold tracking-tight text-foreground">
            Sign in
          </h1>
          <p className="text-sm text-muted-foreground">
            to continue to DiligenceGPT
          </p>
        </div>

        {/* Social Buttons */}
        <div className="grid gap-3">
          <Button
            variant="outline"
            className="w-full h-11 gap-3 justify-center text-sm font-medium border-border/80 hover:bg-accent/50 transition-colors"
            onClick={() => handleSocialLogin("oauth_google")}
            disabled={!!socialLoading}
          >
            {socialLoading === "oauth_google" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <GoogleIcon />
            )}
            Continue with Google
          </Button>

          <Button
            variant="outline"
            className="w-full h-11 gap-3 justify-center text-sm font-medium border-border/80 hover:bg-accent/50 transition-colors"
            onClick={() => handleSocialLogin("oauth_linkedin_oidc")}
            disabled={!!socialLoading}
          >
            {socialLoading === "oauth_linkedin_oidc" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LinkedInIcon />
            )}
            Continue with LinkedIn
          </Button>
        </div>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border/60" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-background px-3 text-muted-foreground uppercase tracking-wider">
              or
            </span>
          </div>
        </div>

        {/* Email Form */}
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email address
            </Label>
            <Input
              id="email"
              placeholder="name@company.com"
              type="email"
              autoCapitalize="none"
              autoComplete="email"
              autoCorrect="off"
              disabled={isLoading}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11"
              required
            />
          </div>
          <Button
            className="w-full h-11 gap-2 font-medium"
            type="submit"
            disabled={isLoading || !email}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Continue with Email
              </>
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground leading-relaxed">
          By continuing, you agree to our{" "}
          <a
            href="https://www.dgpt.io/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 hover:text-foreground transition-colors"
          >
            Terms
          </a>{" "}
          and{" "}
          <a
            href="https://www.dgpt.io/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 hover:text-foreground transition-colors"
          >
            Privacy Policy
          </a>
          .
        </p>

        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{" "}
          <Link
            to="/get-started"
            className="font-medium text-primary hover:underline"
          >
            Get started
          </Link>
        </p>
      </div>
    );
  }

  /* ─── OTP Verification Step ─── */
  return (
    <div className="w-full space-y-6">
      <div className="space-y-1.5">
        <h1 className="text-[1.75rem] font-bold tracking-tight text-foreground">
          Check your email
        </h1>
        <p className="text-sm text-muted-foreground">
          Enter the code sent to{" "}
          <span className="font-medium text-foreground">{email}</span>
        </p>
      </div>

      <form onSubmit={handleOtpSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="otp" className="text-sm font-medium">
            Verification code
          </Label>
          <Input
            id="otp"
            placeholder="Enter 6-digit code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={isLoading}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            className="h-12 text-center text-xl tracking-[0.4em] font-mono"
            maxLength={6}
            required
            autoFocus
          />
        </div>
        <Button
          className="w-full h-11 gap-2 font-medium"
          type="submit"
          disabled={isLoading || otp.length < 6}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              Verify &amp; Sign In
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </form>

      <button
        type="button"
        className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        onClick={() => {
          setStep("email");
          setOtp("");
        }}
        disabled={isLoading}
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Use a different email
      </button>
    </div>
  );
}
